// helpers
import { bindEvents, getOffsetBounds } from '../../../assets/js/helpers';

// controllers
import AnimationController from '../../../assets/js/controller/animation';

// dom selector (prefixed by data-)
const selector = 'video-player';

export default class VideoPlayer {

	constructor(section) {
		this.el = {
			section,
			video: section.querySelector(`.${selector} video`),
			close: section.querySelector(`.${selector}__close`),
			progress: section.querySelector(`.${selector}__progress`),
			progressBar: section.querySelector(`.${selector}__progress-bar`),
			timeCurrent: section.querySelector(`.${selector}__time-current`),
			timeDuration: section.querySelector(`.${selector}__time-duration`),
			audio: section.querySelector(`.${selector}__audio`)
		};

		this.duration = 0;
		this.currentTime = 0;
		this.progress = 0;
		this.formattedTime = '';
		this.muted = false;
		this.seeking = false;

		this.animation = null;
		this.timeouts = null;

		this.bounds = null;

		// bind events to class scope
		this.events = bindEvents(this);

		// LET'S MOSEY
		this.init();
	}

	static get selector() {
		return selector;
	}

	init() {
		this.el.section.style.top = `${window.scrollY}px`;
		this.removeEventListeners();
		this.addEventListeners();
		this.measure();
		if (!this.duration) this.setMeta();
	}

	measure() {
		this.bounds = getOffsetBounds(this.el.section);
	}

	// event listeners

	addEventListeners() {
		this.el.section.addEventListener('show', this.events._onShow);
		this.el.close.addEventListener('click', this.events._onCloseClick);

		this.el.video.addEventListener('click', this.events._onVideoClick);
		this.el.video.addEventListener('play', this.events._onVideoPlay);
		this.el.video.addEventListener('pause', this.events._onVideoPause);
		this.el.video.addEventListener('volumechange', this.events._onVolumeChange);

		this.el.progress.addEventListener('mousedown', this.events._onMouseDown);
		this.el.progress.addEventListener('mousemove', this.events._onMouseMove);
		this.el.progress.addEventListener('mouseup', this.events._onMouseUp);

		this.el.audio.addEventListener('click', this.events._onAudioClick);
	}

	removeEventListeners() {
		this.el.section.removeEventListener('show', this.events._onShow);
		this.el.close.removeEventListener('click', this.events._onCloseClick);

		this.el.video.removeEventListener('click', this.events._onVideoClick);
		this.el.video.removeEventListener('play', this.events._onPlay);
		this.el.video.removeEventListener('pause', this.events._onPause);
		this.el.video.removeEventListener('volumechange', this.events._onVolumeChange);

		this.el.progress.removeEventListener('mousedown', this.events._onMouseDown);
		this.el.progress.removeEventListener('mousemove', this.events._onMouseMove);
		this.el.progress.removeEventListener('mouseup', this.events._onMouseUp);

		this.el.audio.removeEventListener('click', this.events._onAudioClick);
	}

	// listener methods

	_onShow() {
		this.init();
		this.el.video.play();
	}

	_onCloseClick() {
		this.hide();
		this.el.video.pause();
	}

	_onVideoClick() {
		this.el.video.paused ? this.el.video.play() : this.el.video.pause();
	}

	_onVideoPlay() {
		this.start();
	}

	_onVideoPause() {
		this.stop();
	}

	_onMouseDown(e) {
		e.preventDefault();
		this.seeking = true;
		this.el.video.currentTime = e.pageX / window.innerWidth * this.duration;
	}

	_onMouseMove(e) {
		if (this.seeking) {
			e.preventDefault();
			this.el.video.currentTime = (e.pageX - this.bounds.left) / this.bounds.width * this.duration;
		}
	}

	_onMouseUp() {
		if (this.seeking) {
			this.seeking = false;
		}
	}

	_onVolumeChange(e) {
		this.toggleAudio(e);
	}

	_onAudioClick(e) {
		e.preventDefault();
		this.el.video.volume = !this.muted ? 0 : 1;
	}

	// animation methods

	update() {
		if (this.currentTime !== this.el.video.currentTime) {
			this.currentTime = this.el.video.currentTime;
			this.progress = this.currentTime / this.duration;
			this.formattedTime = this.formatTime(this.currentTime);
			return true;
		}
	}

	render() {
		this.el.timeCurrent.innerText = this.formattedTime;
		this.el.progressBar.style.transform = `scaleX(${this.progress})`;
	}

	start() {
		this.animation = AnimationController.set({
			update: this.update.bind(this),
			render: this.render.bind(this)
		});
	}

	stop() {
		AnimationController.clear(this.animation);
	}

	hide() {
		if (this.el.section.classList.contains('is-shown')) this.el.section.classList.remove('is-shown');
		this.el.section.dispatchEvent(new CustomEvent('hide'));
	}

	// video methods

	setMeta() {
		this.duration = this.el.video.duration;
		this.el.timeDuration.innerText = this.formatTime(this.duration);
	}

	toggleAudio(e) {
		if (!e.target.volume) {
			this.muted = true;
			this.el.audio.classList.add('is-muted');
		} else {
			this.muted = false;
			this.el.audio.classList.remove('is-muted');
		}
	}

	// utils

	formatTime(time) {
		const numbers = parseInt(time, 10);

		let hours = Math.floor(numbers / 3600),
			minutes = Math.floor((numbers - (hours * 3600)) / 60),
			seconds = numbers - (hours * 3600) - (minutes * 60);

		if (hours < 10) hours = `0${hours}`;
		if (minutes < 10) minutes = `0${minutes}`;
		if (seconds < 10) seconds = `0${seconds}`;

		return hours === '00' ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
	}

	// destroy

	destroy() {
		if (this.timeout) clearTimeout(this.timeout);
		this.stop();
		this.removeEventListeners();
	}

}
